import { Federacion, FederacionJSON } from '../Federacion/Federacion';

export interface ClubJSON {
  club_id: string | null;
  club_nombre: string;
  club_direccion: string;
  club_proid: number;
  club_localidad: string;
  club_cod_postal: string;
  club_zona: string;
  club_nro_telefono: string;
  club_activo: boolean;
  club_escudo: string;
  club_cjid_default: number;
  club_fedid: number;
  federacion: FederacionJSON;
}

export interface DetalleJugadoresPorRamaJSON {
  rama_nombre: string;
  categorias: CatJSON[];
}

export interface CatJSON {
  cat_nombre: string;
  cat_hab: JugadoresActivosJson[];
  cat_tot_hab: number;
  cat_inhab: JugadoresInactivosJson;
  cat_tot_inhab: number;
  cat_tot: number;
  cat_maxima_categoria: boolean;
}

export interface JugadoresActivosJson {
  cantidadJugadores: number;
  nombreDivision: string;
  idDivision: string;
}

export interface JugadoresInactivosJson {
  cantidadPendientes: number;
  cantidadPendientesHabilitacion: number;
  cantidadInactivos: number;
  cantidadHabilitados: number;
  cantidadRechazados: number;
}

export class Club {
  protected constructor(
    private id: string | null,
    private nombre: string,
    private direccion: string,
    private proid: number,
    private localidad: string,
    private codPostal: string,
    private zona: string,
    private nroTelefono: string,
    private activo: boolean,
    private escudo: string,
    private canidDefault: number,
    private fedid: number,
    private federacion: Federacion
  ) {}

  static createFrom(
    id: string | null,
    nombre: string,
    direccion: string,
    proid: number,
    localidad: string,
    codPostal: string,
    zona: string,
    nroTelefono: string,
    activo: boolean,
    escudo: string,
    canidDefault: number,
    fedid: number,
    federacion: Federacion
  ) {
    return new this(
      id,
      nombre,
      direccion,
      proid,
      localidad,
      codPostal,
      zona,
      nroTelefono,
      activo,
      escudo,
      canidDefault,
      fedid,
      federacion
    );
  }

  isIdentifiedBy(anId: string): boolean {
    return this.id === anId;
  }

  isIdentifiedAs(aEntity: Club) {
    if (this.id) {
      return aEntity.isIdentifiedBy(this.id);
    } else {
      return false;
    }
  }

  static fromJSON(entity: ClubJSON) {
    return Club.createFrom(
      entity.club_id,
      entity.club_nombre,
      entity.club_direccion,
      entity.club_proid,
      entity.club_localidad,
      entity.club_cod_postal,
      entity.club_zona,
      entity.club_nro_telefono,
      entity.club_activo,
      entity.club_escudo,
      entity.club_cjid_default,
      entity.club_fedid,
      Federacion.fromJSON(entity.federacion)
    );
  }

  toJSON(): ClubJSON {
    return {
      club_id: this.getId(),
      club_nombre: this.getNombre(),
      club_direccion: this.getDireccion(),
      club_proid: this.getProid(),
      club_localidad: this.getLocalidad(),
      club_cod_postal: this.getCodPostal(),
      club_zona: this.getZona(),
      club_nro_telefono: this.getNroTelefono(),
      club_activo: this.getActivo(),
      club_escudo: this.getEscudo(),
      club_cjid_default: this.getCanidDefault(),
      club_fedid: this.getFedid(),
      federacion: this.getFederacion().toJSON(),
    };
  }

  getId(): string | null {
    return this.id;
  }

  getNombre(): string {
    return this.nombre;
  }

  getDireccion(): string {
    return this.direccion;
  }

  getProid(): number {
    return this.proid;
  }

  getLocalidad(): string {
    return this.localidad;
  }

  getCodPostal(): string {
    return this.codPostal;
  }

  getZona(): string {
    return this.zona;
  }

  getNroTelefono(): string {
    return this.nroTelefono;
  }

  getActivo(): any {
    return this.activo;
  }

  getEscudo(): string {
    return this.escudo;
  }

  getCanidDefault(): number {
    return this.canidDefault;
  }

  getFedid(): number {
    return this.fedid;
  }

  getFederacion(): Federacion {
    return this.federacion;
  }

  setId(value: string | null): void {
    this.id = value;
  }

  setNombre(value: string): void {
    this.nombre = value;
  }

  setDireccion(value: string): void {
    this.direccion = value;
  }

  setProid(value: number): void {
    this.proid = value;
  }

  setLocalidad(value: string): void {
    this.localidad = value;
  }

  setCodPostal(value: string): void {
    this.codPostal = value;
  }

  setZona(value: string): void {
    this.zona = value;
  }

  setNroTelefono(value: string): void {
    this.nroTelefono = value;
  }

  setActivo(value: any): void {
    this.activo = value;
  }

  setEscudo(value: string): void {
    this.escudo = value;
  }

  setCanidDefault(value: number): void {
    this.canidDefault = value;
  }

  setFedid(value: number): void {
    this.fedid = value;
  }

  setFederacion(value: Federacion): void {
    this.federacion = value;
  }

  nameLike(val: string): boolean {
    if (this.getNombre()) {
      return this.getNombre().toLowerCase().includes(val.toLowerCase());
    } else return true;
  }
}

export class DetalleJugadores {
  protected constructor(private rama_nombre: string, private categorias: Cat[]) {}

  static createFrom(rama_nombre: string, categorias: Cat[]) {
    return new this(rama_nombre, categorias);
  }

  static fromJSON(entity: DetalleJugadoresPorRamaJSON) {
    return DetalleJugadores.createFrom(
      entity.rama_nombre,
      entity.categorias.map((c) => {
        return Cat.fromJSON(c);
      })
    );
  }

  getRama_nombre(): string {
    return this.rama_nombre;
  }

  getCategorias(): Cat[] {
    return this.categorias;
  }
}

export class Cat {
  protected constructor(
    private cat_nombre: string,
    private cat_hab: JugadoresActivos[],
    private cat_tot_hab: number,
    private cat_inhab: JugadoresInactivos,
    private cat_tot_inhab: number // private cat_tot: number
  ) {}

  static createFrom(
    cat_nombre: string,
    cat_hab: JugadoresActivos[],
    cat_tot_hab: number,
    cat_inhab: JugadoresInactivos,
    cat_tot_inhab: number
  ) {
    return new this(cat_nombre, cat_hab, cat_tot_hab, cat_inhab, cat_tot_inhab);
  }

  static fromJSON(entity: CatJSON) {
    return Cat.createFrom(
      entity.cat_nombre,
      entity.cat_hab.map((d) => {
        return JugadoresActivos.fromJSON(d);
      }),
      entity.cat_tot_hab,
      JugadoresInactivos.fromJSON(entity.cat_inhab),
      entity.cat_tot_inhab
    );
  }

  getCat_nombre(): string {
    return this.cat_nombre;
  }

  getCat_hab(): JugadoresActivos[] {
    return this.cat_hab;
  }

  getCat_tot_hab(): number {
    return this.cat_tot_hab;
  }

  getCat_inhab(): JugadoresInactivos {
    return this.cat_inhab;
  }

  getCat_tot_inhab(): number {
    return this.cat_tot_inhab;
  }
}

export class JugadoresActivos {
  protected constructor(
    private cantidadJugadores: number,
    private nombreDivision: string,
    private idDivision: string
  ) {}

  static createFrom(cantidadJugadores: number, nombreDivision: string, idDivision: string) {
    return new this(cantidadJugadores, nombreDivision, idDivision);
  }

  static fromJSON(entity: JugadoresActivosJson) {
    return JugadoresActivos.createFrom(entity.cantidadJugadores, entity.nombreDivision, entity.idDivision);
  }

  getCantidadJugadores(): number {
    return this.cantidadJugadores;
  }

  getNombreDivision(): string {
    return this.nombreDivision;
  }

  getIdDivision(): string {
    return this.idDivision;
  }
}

export class JugadoresInactivos {
  protected constructor(
    private cantidadPendientes: number,
    private cantidadPendientesHabilitacion: number,
    private cantidadInactivos: number
  ) {}

  static createFrom(
    cantidadPendientes: number,
    cantidadPendientesHabilitacion: number,
    cantidadInactivos: number
  ) {
    return new this(cantidadPendientes, cantidadPendientesHabilitacion, cantidadInactivos);
  }

  static fromJSON(entity: JugadoresInactivosJson) {
    return JugadoresInactivos.createFrom(
      entity.cantidadPendientes,
      entity.cantidadPendientesHabilitacion,
      entity.cantidadInactivos
    );
  }

  getCantidadPendientes(): number {
    return this.cantidadPendientes;
  }

  getCantidadPendientesHabilitacion(): number {
    return this.cantidadPendientesHabilitacion;
  }

  getCantidadInactivos(): number {
    return this.cantidadInactivos;
  }
}
